import { useEffect, useMemo, useRef, useState } from "react";
import { format, startOfMonth, startOfYear } from "date-fns";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Card,
  CardBody,
  Checkbox,
  Flex,
  HStack,
  Heading,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  NumberInput,
  NumberInputField,
  Spacer,
  Tag,
  TagLabel,
  TagLeftIcon,
  Td,
  Text,
  Tr,
  VStack,
  UseToastOptions,
  useDisclosure,
  useToast,
  useToken,
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import { v4 as uuidv4 } from "uuid";
import { createColumnHelper } from "@tanstack/react-table";
import { rankItem, rankings } from "@tanstack/match-sorter-utils";
import { DataTable } from "../../components/data-table";
import {
  aerzteTreuhandLogo,
  compareStrings,
  dateToApiString,
  deleteItem,
  fetchReceipt,
  formatAmount,
  formatDate,
  getBookkeepingYear,
  getLocalDate,
  getTaxAmount,
  getTransactionCategoryIcon,
  getUserName,
  isPretrialActive,
  openInNewTab,
  queryList,
  showBillingModal,
  showSettings,
  stringWithoutManualLineBreaks,
} from "../../utils";
import { State } from "../../types";

import IconFilePdf from "../../assets/icons/file-pdf.svg";
import { ReactComponent as IconEdit } from "../../assets/icons/pencil.svg";
import { ReactComponent as IconCancel } from "../../assets/icons/xClose.svg";
import IconSettings from "../../assets/icons/settings-01.svg";
import IconUsersX from "../../assets/icons/users-x.svg";

import DownloadButton from "../../components/download-button";
import DateSelector from "../../components/date-selector";
import DeleteButton from "../../components/delete-button";
import {
  AccountingSystem,
  AccountingSystemConfig,
  AccountingSystemConfigsByBookkeepingIdQuery,
  AccountingSystemConfigsByBookkeepingIdQueryVariables,
  AttachmentsByTransactionIdQuery,
  AttachmentsByTransactionIdQueryVariables,
  CurrencyCode,
  ListUsersQuery,
  ListUsersQueryVariables,
  OrganizationType,
  TaxType,
  TaxesByTransactionIdQuery,
  TaxesByTransactionIdQueryVariables,
  ModelTransactionFilterInput,
  TransactionStatus,
  UpdateBookkeepingYearMutation,
  UpdateTransactionMutation,
  UserAppRole,
  UserOrganizationRole,
} from "../../API";
import {
  Attachment,
  BookingAccount,
  BookkeepingFull,
  BookkeepingYear,
  Tax,
  TaxRate,
  Transaction,
  TransactionCategory,
  TransactionFull,
  User,
} from "../../models";
import {
  attachmentsByTransactionId,
  listUsers,
  accountingSystemConfigsByBookkeepingId,
  taxesByTransactionId,
} from "../../graphql/queries";
import { listTransactionsWithBill } from "../../graphql/custom-queries";
import StatusChip from "../../components/status-chip";
import ShowExportedToggle from "../../components/show-exported-toggle";
import DateRangeSelector from "../../components/date-range-selector";
import { DateRange } from "react-day-picker";
import { MultiSelect, MultiSelectOption } from "../../components/select";
import NoOrganization from "../../components/no-organization";
import { API } from "aws-amplify";
import { GraphQLQuery } from "@aws-amplify/api";
import {
  updateBookkeepingYear,
  updateTransaction,
} from "../../graphql/mutations";
import { useContextTypeTransactions } from "./dashboard-layout";
import { useNavigate } from "react-router-dom";
import { routeOnboardingBilling, routeSettingsBookkeeping } from "../routes";
import { useTransactionCategories } from "../../hooks/use-transaction-categories";
import { useUserOrganizations } from "../../hooks/use-user-organizations";
import { useUsersByBookkeeping } from "../../hooks/use-users-by-bookkeeping";
import Tooltip from "../../components/tooltip";
import SignOutLinkButton from "../../components/sign-out-link-button";
import { useTransactionReferencesByUsers } from "../../hooks/use-transaction-references-by-users";
import { Select, Option as SelectOption } from "../../components/select";
import DropzoneOverlay from "../../components/dropzone-overlay";
import VerifyReceiptModal, {
  VerifyReceiptMode,
  VerifyReceiptResult,
  VerifyReceiptTax,
  VerifyReceiptValues,
} from "../../components/verify-receipt-modal";
import { isTransactionEditingEnabled } from "../../utils/transactions/isTransactionEditingEnabled";
import {
  OcrResult,
  deriveTaxAmount,
  isHeicFile,
  isSupportedReceiptFile,
  matchOcrTaxesToTaxRates,
  performOcr,
  uploadReceiptToS3,
} from "../../utils/receipt-upload";
import {
  createReceiptTransaction,
  updateReceiptTransaction,
} from "../../utils/create-transaction";
import { Fx, isFxCaptureStale } from "../../utils/exchange-rate";

const emptyFunction = () => {};
const reloadWindow = () => window.location.reload();

const typeNameSingular = "Transaktion";
const typeNamePlural = "Transaktionen";

// BAZG quotes 74 currencies; the schema offers 160. When a receipt lands in one
// of the other 86 the row still saves, but with no rate — and the totals and
// the export then read the raw foreign amount as though it were CHF
// (86c9pkyzj). Say so instead of showing a silently wrong number.
const missingRateToast = (
  currencyCode: CurrencyCode,
  baseCurrency: CurrencyCode
): UseToastOptions => ({
  title: `Kein Wechselkurs für ${currencyCode.toUpperCase()} gefunden`,
  description: `Der Beleg wurde gespeichert, aber nicht in ${baseCurrency.toUpperCase()} umgerechnet.`,
  status: "warning",
  duration: null,
  isClosable: true,
});

// The row is committed but the ÄT Kassenbuch balance it should have moved is
// not. Nothing retries it and `balance` has no editor (settings only exposes
// balanceStart), so the number stays wrong until someone notices. Reporting a
// plain "gespeichert" here would hide an accounting error, and reporting
// "Speichern fehlgeschlagen" would be a lie that invites a duplicate.
const balanceUpdateFailedToast: UseToastOptions = {
  title: "Saldo nicht aktualisiert",
  description: "Die Transaktion wurde gespeichert, ihr Betrag fehlt aber im Saldo.",
  status: "warning",
  duration: null,
  isClosable: true,
};

const columnHelper = createColumnHelper<TransactionFull>();

const currencyCodesDefault = [CurrencyCode.chf, CurrencyCode.eur];
const currencyCodesExtended = [
  ...currencyCodesDefault,
  CurrencyCode.usd,
  CurrencyCode.gbp,
  CurrencyCode.brl,
  CurrencyCode.cny,
  CurrencyCode.jpy,
  CurrencyCode.mxn,
  CurrencyCode.cop,
  CurrencyCode.sek,
  CurrencyCode.dkk,
  CurrencyCode.nok,
  CurrencyCode.isk,
  CurrencyCode.aud,
].sort((a, b) => a.localeCompare(b));

const NO_REFERENCE = "__NO_REFERENCE__";

const Transactions = () => {
  const navigate = useNavigate();

  const [gray700, success700, error700] = useToken("colors", [
    "gray.700",
    "success.700",
    "error.700",
  ]);

  const {
    user,
    organizations,
    selectedOrganization,
    selectedBookkeepingFull,
    setSelectedBookkeepingFull,
    includeDeletedUsers,
    setIncludeDeletedUsers,
  } = useContextTypeTransactions();

  // The bookkeeping's configured base, not a CHF constant — at least one
  // customer runs a non-CHF AccountingSystemConfig, and resolveFx/
  // getBaseCurrencyAmount already handle a non-CHF base. The layout's list
  // query does not select the config, so it is fetched here (same pattern as
  // DownloadButton). CHF until it answers — the app defaults the same way
  // (getBaseCurrency, globals.dart:746).
  const [baseCurrency, setBaseCurrency] = useState<CurrencyCode>(
    CurrencyCode.chf
  );
  useEffect(() => {
    let cancelled = false;
    setBaseCurrency(CurrencyCode.chf);
    if (!selectedBookkeepingFull?.id) return;
    queryList<
      AccountingSystemConfig,
      AccountingSystemConfigsByBookkeepingIdQueryVariables,
      AccountingSystemConfigsByBookkeepingIdQuery
    >(accountingSystemConfigsByBookkeepingId, {
      bookkeepingId: selectedBookkeepingFull.id,
    })
      .then((configs) => {
        if (!cancelled && configs.length > 0 && configs[0].baseCurrency) {
          setBaseCurrency(configs[0].baseCurrency);
        }
      })
      .catch((error) =>
        console.error("Error fetching AccountingSystemConfig", error)
      );
    return () => {
      cancelled = true;
    };
  }, [selectedBookkeepingFull?.id]);

  const optionsBookingAccounts: SelectOption[] | undefined = useMemo(
    () =>
      selectedBookkeepingFull?.bookingAccounts
        .filter((bookingAccount) => !bookingAccount.deletedAt)
        .map((bookingAccount) => ({
          value: bookingAccount.id,
          label: stringWithoutManualLineBreaks(bookingAccount.name),
        }))
        .sort((a, b) => a.label.localeCompare(b.label, "de-CH")),
    [selectedBookkeepingFull]
  );

  const { transactionCategories } = useTransactionCategories();
  const [selectedUsers, setSelectedUsers] = useState<MultiSelectOption[]>();
  const [allUsersSelected, setAllUsersSelected] = useState(false);
  const [selectedBookingAccounts, setSelectedBookingAccounts] =
    useState<MultiSelectOption[]>();
  const [allBookingAccountsSelected, setAllBookingAccountsSelected] = useState(false);
  const [selectedTransactionReferences, setSelectedTransactionReferences] =
    useState<MultiSelectOption[]>();
  const [allTransactionReferencesSelected, setAllTransactionReferencesSelected] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showExported, setShowExported] = useState(true);
  const { userOrganizations } = useUserOrganizations({
    selectedOrganization,
    includeDeleted: includeDeletedUsers,
  });
  const { users } = useUsersByBookkeeping({
    userOrganizations,
    selectedOrganization,
    selectedBookkeepingFull,
    includeDeleted: includeDeletedUsers,
  });

  const optionsUsers: SelectOption[] | undefined = useMemo(
    () =>
      users
        ?.slice()
        .sort((a, b) => getUserName(a).localeCompare(getUserName(b), "de-CH"))
        .map((user) => ({
          value: user.id,
          label: getUserName(user),
        })),
    [users]
  );

  const { transactionReferences } = useTransactionReferencesByUsers(users);

  const optionsTransactionReferences: SelectOption[] | undefined = useMemo(
    () => {
      if (!transactionReferences) return undefined;
      const uniqueNames = [...new Set(
        transactionReferences
          .filter((reference) => !reference.deletedAt)
          .map((reference) => stringWithoutManualLineBreaks(reference.name))
      )].sort((a, b) => a.localeCompare(b, "de-CH"));
      return [
        { value: NO_REFERENCE, label: "Keine Referenz" },
        ...uniqueNames.map((name) => ({ value: name, label: name })),
      ];
    },
    [transactionReferences]
  );

  const [transactions, setTransactions] = useState<TransactionFull[]>();
  const [state, setState] = useState<State>("initial");
  // Fetch trigger, separate from `state`: a monotonic counter can never
  // collapse into a no-op the way the state string can. When the fetch effect
  // consumes "loading" (queues "success") and a clear-effect re-arms "loading"
  // in the same pass, the two updates cancel to a value-identical state and
  // Object.is bails — no render, no re-fetch, skeleton forever. Seen on the
  // very first load, where users/options/bookkeeping churn lands exactly so.
  // An increment always changes, so a requested fetch always runs.
  const [fetchNonce, setFetchNonce] = useState(0);
  const processedFetchNonceRef = useRef(0);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    const today = new Date();
    return {
      from: getLocalDate(startOfMonth(today)),
      to: getLocalDate(today),
    };
  });
  const [hasEarnings, setHasEarnings] = useState<boolean>();
  const [hasCurrencies, setHasCurrencies] = useState<boolean>();
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [updating, setUpdating] = useState(false);

  const [transactionIdsSelected, setTransactionIdsSelected] = useState<
    string[]
  >([]);
  const [transactionsUpdated, setTransactionsUpdated] = useState<
    TransactionFull[]
  >([]);

  // Refs mirror the two pieces of state above. They're read inside the
  // data-loading useEffect to filter post-fetch resets without putting the
  // state into the effect's dependency array — which would otherwise
  // re-trigger fetchTransactions() on every checkbox toggle.
  const transactionIdsSelectedRef = useRef(transactionIdsSelected);
  const transactionsUpdatedRef = useRef(transactionsUpdated);
  useEffect(() => {
    transactionIdsSelectedRef.current = transactionIdsSelected;
  }, [transactionIdsSelected]);
  useEffect(() => {
    transactionsUpdatedRef.current = transactionsUpdated;
  }, [transactionsUpdated]);

  const transactionAmountSign = useMemo(
    () => (transaction: TransactionFull) =>
      hasEarnings &&
      transaction.bookingAccount.taxType !== TaxType.earnings &&
      // Accountants will always see the amount as positive
      user?.role !== UserAppRole.accountant
        ? -1
        : 1,
    [hasEarnings, user]
  );

  // --- Receipt dropzone flow -------------------------------------------
  const toast = useToast();
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyInitialValues, setVerifyInitialValues] =
    useState<VerifyReceiptValues>();
  const [verifyPreview, setVerifyPreview] = useState<{
    url: string;
    isPdf: boolean;
  }>();
  // Fresh drop: the uploaded file waiting for its transaction.
  const [verifyFileId, setVerifyFileId] = useState<string>();
  // Transaction id for the fresh-drop session, minted once at drop time: a
  // retry after an ambiguous failure (commit succeeded, response lost) reuses
  // it, so createReceiptTransaction can recognize its own previous attempt
  // instead of minting a duplicate row.
  const [verifyDraftId, setVerifyDraftId] = useState<string>();
  const [verifyPreviewPending, setVerifyPreviewPending] = useState(false);
  // Row-click: the stored row the modal holds — extracted to verify, anything
  // else to edit or read-only (verifyMode / verifyReadOnly derive from it).
  const [verifyTransaction, setVerifyTransaction] = useState<TransactionFull>();
  const [importing, setImporting] = useState(false);
  // The hidden picker behind the toolbar «Hochladen» button.
  const uploadInputRef = useRef<HTMLInputElement>(null);
  // Defaults to true so users with inbox items land on them first — the app
  // pins its inbox section on top the same way. With zero extracted rows the
  // effective flag below turns it off, so everyone else sees the normal list.
  const [showOnlyExtracted, setShowOnlyExtracted] = useState(true);
  // Blocks closeVerifyModal while a save is in flight — closing then would
  // count the drop as abandoned and create a duplicate extracted transaction.
  const verifySavingRef = useRef(false);
  // Invalidates async work (receipt preview fetch) from a previous
  // open/close cycle so it can't attach to the wrong transaction.
  const verifyRequestIdRef = useRef(0);

  // Picker options = static extended list plus any currency present on
  // loaded transactions (OCR can store any of the 160 schema codes; the
  // shortlist would otherwise hide existing values like AED/HKD/INR/...).
  const optionsCurrencyCodesEffective: SelectOption[] = useMemo(() => {
    const codes = new Set<CurrencyCode>(currencyCodesExtended);
    transactions?.forEach((transaction) => codes.add(transaction.currencyCode));
    // The receipt being verified counts too, not just rows already loaded. OCR
    // reads any of the 160 codes, so a fresh drop in one outside the shortlist —
    // and absent from the table — left its own currency out of its own picker:
    // the Select rendered blank while the value it could not show was the one
    // being saved, and now the one a rate gets fetched for.
    if (verifyInitialValues) codes.add(verifyInitialValues.currencyCode);
    return Array.from(codes)
      .sort((a, b) => a.localeCompare(b))
      .map((currency) => ({ value: currency, label: currency.toUpperCase() }));
  }, [transactions, verifyInitialValues]);


  // Bexio bookkeepings sync from draft; everything else books directly
  // (same rule as the app's saveTransaction). The app recomputes the status on
  // EVERY save the same way (globals.dart:431) — editing a booked Bexio row
  // deliberately flips it back to draft and re-arms the sync.
  const verifiedStatus =
    selectedBookkeepingFull?.accountingSystem === AccountingSystem.bexio
      ? TransactionStatus.draft
      : TransactionStatus.booked;

  // The app computes editingEnabled per row in the list and hands it to the
  // detail screen (transactions_list.dart:206-216), gating on the VIEWED
  // user's role in the selected organization. The web shows many users' rows
  // at once, so the viewed user is the row's owner. userOrganizations excludes
  // accountant memberships (the hook filters role ne accountant); the
  // logged-in user's own memberships cover their own rows as fallback.
  const rowOwnerOrganizationRole = (
    transaction: TransactionFull
  ): UserOrganizationRole | undefined =>
    userOrganizations?.find(
      (userOrganization) =>
        userOrganization.userId === transaction.userId &&
        userOrganization.organizationId === selectedOrganization?.id
    )?.role ??
    (transaction.userId === user?.id
      ? user.userOrganizations?.find(
          (userOrganization) =>
            userOrganization.organizationId === selectedOrganization?.id
        )?.role
      : undefined);

  // Writes require the viewer to be the row's owner or to hold a
  // write-granting role — Transaction @auth: owner via userId, groups via
  // authGroups (accountants + org admins); HEADS are read-only via
  // authGroupsRead. The app's single-user screens never surface this case,
  // but the web table shows heads other users' rows: without this check they
  // would get an editable form whose save can only fail authorization.
  const viewerCanWriteTransaction = (transaction: TransactionFull): boolean => {
    if (transaction.userId === user?.id) return true;
    const viewerRole = user?.userOrganizations?.find(
      (userOrganization) =>
        userOrganization.organizationId === selectedOrganization?.id
    )?.role;
    return (
      viewerRole === UserOrganizationRole.accountant ||
      viewerRole === UserOrganizationRole.admin
    );
  };

  // Derived at render, never stored — it cannot drift from the row the modal
  // holds. Extracted rows always take the verify flow; the gate can still
  // render them read-only (disabled Bestätigen), same as the app.
  const verifyMode: VerifyReceiptMode = !verifyTransaction
    ? "create"
    : verifyTransaction.status === TransactionStatus.extracted
      ? "verify"
      : "edit";
  const verifyReadOnly = verifyTransaction
    ? !(
        isTransactionEditingEnabled(
          verifyTransaction,
          rowOwnerOrganizationRole(verifyTransaction)
        ) && viewerCanWriteTransaction(verifyTransaction)
      )
    : false;

  // Per row owner AND per the selected bookkeeping, like the app's
  // userBookkeeping references (detail_screen.dart:217): references hang off
  // a UserBookkeeping, and a user in several bookkeepings must not link a
  // transaction to another bookkeeping's reference — the hook fetches by
  // authUserId only. A fresh drop belongs to the current user, a row-click to
  // the row's owner. Known limitation: the hook excludes soft-deleted
  // records, so a row whose current reference was deleted shows the
  // placeholder here (the stored id still survives an unrelated save — the
  // seed keeps it and only a pick overwrites it).
  const verifyOptionsTransactionReferences: SelectOption[] | undefined =
    useMemo(() => {
      const ownerId = verifyTransaction?.userId ?? user?.id;
      if (!ownerId || !transactionReferences) return undefined;
      const ownerUserBookkeepingId =
        selectedBookkeepingFull?.userBookkeepings?.find(
          (userBookkeeping) => userBookkeeping.userId === ownerId
        )?.id;
      if (!ownerUserBookkeepingId) return undefined;
      return transactionReferences
        .filter(
          (transactionReference) =>
            transactionReference.userBookkeepingId === ownerUserBookkeepingId
        )
        .map((transactionReference) => ({
          value: transactionReference.id,
          label: stringWithoutManualLineBreaks(transactionReference.name),
        }));
    }, [transactionReferences, verifyTransaction, user, selectedBookkeepingFull]);

  const mostUsedBookingAccountId: string | undefined = useMemo(() => {
    const validIds = new Set(
      (optionsBookingAccounts ?? []).map((option) => option.value as string)
    );
    const counts = new Map<string, number>();
    for (const transaction of transactions ?? []) {
      // Extracted rows carry this very default — counting them would make
      // the guess self-reinforcing; only user-confirmed bookings count.
      if (transaction.status === TransactionStatus.extracted) continue;
      if (!validIds.has(transaction.bookingAccountId)) continue;
      counts.set(
        transaction.bookingAccountId,
        (counts.get(transaction.bookingAccountId) ?? 0) + 1
      );
    }
    let bestId: string | undefined;
    let bestCount = 0;
    counts.forEach((count, id) => {
      if (count > bestCount) {
        bestCount = count;
        bestId = id;
      }
    });
    return bestId ?? (optionsBookingAccounts?.[0]?.value as string | undefined);
  }, [transactions, optionsBookingAccounts]);

  const extractedCount = useMemo(
    () =>
      (transactions ?? []).filter(
        (transaction) => transaction.status === TransactionStatus.extracted
      ).length,
    [transactions]
  );

  // Derived instead of an effect: state === "success" is set before the
  // async fetch resolves, so an effect would clear the filter while the
  // refetch is still in flight. A stale true with zero extracted rows is
  // harmless — the filter only applies while something is left to verify.
  const showOnlyExtractedEffective = showOnlyExtracted && extractedCount > 0;

  // The selection is a delete target, so it must never outlive the rows it was
  // made against. The post-fetch prune intersects with what was FETCHED, which
  // is not what is displayed: the extracted inbox deliberately over-fetches
  // across every bookkeeping year (b0520f44), and these filters narrow the table
  // without refetching anything. Select 50, type "Migros", delete — and the 48
  // rows that scrolled out go with them, none of them ever on screen. The header
  // checkbox even renders unchecked while they are armed, because 50 !== 2.
  //
  // Cleared rather than intersected: "visible" is precisely the computation that
  // is already wrong here, and a delete path should not have to get it right a
  // second time. Re-selecting after changing a filter is the cost.
  // `editing` is included so Abbrechen disarms too — it hides the checkboxes
  // while leaving the delete button live.
  //
  // dateRange is here despite triggering a refetch, and it is the one that
  // matters most: the post-fetch prune keeps anything still in transactionsAll,
  // and extracted rows are fetched for EVERY bookkeeping year regardless of the
  // window. They survive the prune and are then hidden by the filter — selected,
  // invisible, and deletable from years the table can never render.
  //
  // These are exactly filteredTransactions' dependencies minus `transactions`
  // itself (covered by the post-fetch prune). If a filter is added there, it
  // belongs here too.
  useEffect(() => {
    setTransactionIdsSelected([]);
  }, [
    searchQuery,
    showExported,
    showOnlyExtractedEffective,
    dateRange,
    editing,
  ]);

  const mapOcrCurrency = (code: string | null): CurrencyCode => {
    const lower = code?.toLowerCase();
    return lower &&
      (Object.values(CurrencyCode) as string[]).includes(lower)
      ? (lower as CurrencyCode)
      : CurrencyCode.chf;
  };

  const ocrToInitialValues = (
    ocr: OcrResult | null,
    file: File
  ): VerifyReceiptValues => {
    const paidAt = ocr?.date
      ? getLocalDate(new Date(ocr.date))
      : getLocalDate(new Date());
    const bookkeepingYear = selectedBookkeepingFull
      ? getBookkeepingYear(selectedBookkeepingFull, paidAt)
      : undefined;
    const taxes =
      // No bookkeeping year for the date → no taxes, same as the app.
      ocr && selectedBookkeepingFull && bookkeepingYear && !bookkeepingYear.hasNetTaxRate
        ? matchOcrTaxesToTaxRates(
            ocr.taxes,
            selectedBookkeepingFull,
            paidAt,
            // Rates are scoped to the booking account's taxType, so the guess
            // below decides which rates can match.
            mostUsedBookingAccountId
          ).map((match) => ({
            taxRateId: match.taxRate.id,
            // What the receipt states — kept unless the user edits it.
            amount: match.amount,
            totalAmount: match.totalAmount,
          }))
        : [];
    return {
      paidAt,
      title: ocr?.vendor ?? file.name.replace(/\.[^.]+$/, ""),
      amount: ocr?.amount ?? null,
      currencyCode: mapOcrCurrency(ocr?.currencyCode ?? null),
      bookingAccountId: mostUsedBookingAccountId,
      // OCR knows nothing about references — the user picks one if they want.
      transactionReferenceId: undefined,
      taxes,
    };
  };

  // VAT is derived from the gross on save, never taken from OCR or user input
  // — mirrors app getTaxAmount. Single tax has no totalAmount, so the
  // transaction total is the gross.
  const buildReceiptTaxes = (
    taxes: VerifyReceiptTax[],
    transactionAmount: number
  ) =>
    taxes
      .filter((tax) => tax.taxRateId)
      .map((tax) => ({
        taxRateId: tax.taxRateId as string,
        // Receipt's own figure when OCR read one, else derived.
        amount:
          tax.amount ??
          deriveTaxAmount(
            transactionAmount,
            (selectedBookkeepingFull?.taxRates ?? []).find(
              (taxRate) => taxRate.id === tax.taxRateId
            )?.rate ?? 0,
            tax.totalAmount
          ),
        totalAmount: tax.totalAmount,
      }));

  // Returns true when it widened the range — the dateRange change then arms
  // the fetch by itself and the caller skips refreshTransactions(). With the
  // fetchNonce trigger a double-arm would merely fold into one fetch (an
  // increment cannot value-collapse the way the old state-string trigger
  // did), but there is no reason to request twice.
  const ensureDateRangeIncludes = (date: Date): boolean => {
    if (!dateRange?.from || !dateRange?.to) return false;
    if (date >= dateRange.from && date <= dateRange.to) return false;
    setDateRange({
      from: date < dateRange.from ? date : dateRange.from,
      to: date > dateRange.to ? date : dateRange.to,
    });
    return true;
  };

  const refreshTransactions = () => {
    setState("loading");
    setTransactions([]);
    setFetchNonce((nonce) => nonce + 1);
  };

  // ÄT Kassenbuch balance parity with the app's saveTransaction: earnings
  // increase, expenses decrease; propagate to the current year if newer.
  const signedReceiptAmount = (bookingAccountId: string, amount: number) => {
    const bookingAccount = selectedBookkeepingFull?.bookingAccounts.find(
      (account) => account.id === bookingAccountId
    );
    return bookingAccount?.taxType === TaxType.earnings ? amount : -amount;
  };

  const collectReceiptBalanceUpdate = (
    balanceUpdates: Map<BookkeepingYear, number>,
    amountSigned: number,
    paidAt: Date
  ) => {
    if (
      selectedOrganization?.logo !== aerzteTreuhandLogo ||
      !selectedBookkeepingFull
    ) {
      return;
    }
    const bookkeepingYear = getBookkeepingYear(selectedBookkeepingFull, paidAt);
    if (!bookkeepingYear) return;
    balanceUpdates.set(
      bookkeepingYear,
      (balanceUpdates.get(bookkeepingYear) ?? 0) + amountSigned
    );
    const nextBookkeepingYear = getBookkeepingYear(
      selectedBookkeepingFull,
      getLocalDate(new Date())
    );
    if (nextBookkeepingYear && nextBookkeepingYear.year > bookkeepingYear.year) {
      balanceUpdates.set(
        nextBookkeepingYear,
        (balanceUpdates.get(nextBookkeepingYear) ?? 0) + amountSigned
      );
    }
  };

  const applyReceiptBalanceUpdate = async (amountSigned: number, paidAt: Date) => {
    const balanceUpdates = new Map<BookkeepingYear, number>();
    collectReceiptBalanceUpdate(balanceUpdates, amountSigned, paidAt);
    if (balanceUpdates.size > 0) {
      await updateBalances(balanceUpdates);
    }
  };

  const buildReceiptInput = (
    values: VerifyReceiptResult,
    status: TransactionStatus,
    userId: string,
    bookkeepingYearId: string
  ) => ({
    paidAt: dateToApiString(values.paidAt),
    title: values.title,
    amount: values.amount,
    currencyCode: values.currencyCode,
    status,
    bookkeepingYearId,
    bookingAccountId: values.bookingAccountId,
    transactionReferenceId: values.transactionReferenceId,
    userId,
    taxes: values.taxes,
  });

  const processSingleFile = async (file: File) => {
    const requestId = ++verifyRequestIdRef.current;
    const extension = file.name.split(".").pop()?.toLowerCase() ?? "pdf";
    setVerifyTransaction(undefined);
    setVerifyFileId(undefined);
    setVerifyDraftId(uuidv4());
    setVerifyInitialValues(undefined);
    setVerifyPreview({ url: URL.createObjectURL(file), isPdf: extension === "pdf" });
    setVerifyLoading(true);
    setVerifyModalOpen(true);
    try {
      const { fileId, s3Key } = await uploadReceiptToS3(file);
      if (requestId !== verifyRequestIdRef.current) return;
      setVerifyFileId(fileId);
      let ocr: OcrResult | null = null;
      try {
        ocr = await performOcr(s3Key, extension);
      } catch {
        toast({
          title: "Extraktion fehlgeschlagen — Felder bitte manuell ausfüllen",
          status: "warning",
        });
      }
      if (requestId !== verifyRequestIdRef.current) return;
      // Booking account is left empty and required rather than pre-filled with
      // the most-used one: it is a required field the user confirms anyway, and
      // a prefilled guess both invites rubber-stamping (users accept defaults)
      // and biases every receipt toward the dominant account — which would also
      // poison the training data for a future account suggestion. The multi-drop
      // create path keeps the most-used default because the row is written
      // immediately and bookingAccountId is non-null in the schema.
      setVerifyInitialValues({
        ...ocrToInitialValues(ocr, file),
        bookingAccountId: undefined,
      });
    } catch {
      if (requestId !== verifyRequestIdRef.current) return;
      toast({ title: "Upload fehlgeschlagen", status: "error" });
      setVerifyModalOpen(false);
      setVerifyPreview((preview) => {
        if (preview) URL.revokeObjectURL(preview.url);
        return undefined;
      });
    } finally {
      if (requestId === verifyRequestIdRef.current) {
        setVerifyLoading(false);
      }
    }
  };

  const processMultipleFiles = async (files: File[]) => {
    if (!selectedBookkeepingFull || !selectedOrganization || !user) return;
    setImporting(true);
    const toastId = toast({
      title: `0/${files.length} Belege verarbeitet`,
      status: "info",
      duration: null,
    });
    let created = 0;
    let failed = 0;
    let missingRate = 0;
    let done = 0;
    let earliestPaidAt: Date | undefined;
    const processFile = async (file: File) => {
      try {
        const extension = file.name.split(".").pop()?.toLowerCase() ?? "pdf";
        const { fileId, s3Key } = await uploadReceiptToS3(file);
        let ocr: OcrResult | null = null;
        try {
          ocr = await performOcr(s3Key, extension);
        } catch {
          // Extracted status means the user verifies anyway; keep the file.
        }
        const initialValues = ocrToInitialValues(ocr, file);
        let paidAt = initialValues.paidAt as Date;
        let bookkeepingYear = getBookkeepingYear(selectedBookkeepingFull, paidAt);
        if (!bookkeepingYear) {
          // No bookkeeping year for the OCR date — fall back to today so the
          // receipt still lands in the inbox; the user corrects on verify.
          paidAt = getLocalDate(new Date());
          bookkeepingYear = getBookkeepingYear(selectedBookkeepingFull, paidAt);
        }
        if (!bookkeepingYear || !initialValues.bookingAccountId) {
          failed += 1;
          return;
        }
        const { fx } = await createReceiptTransaction(
          {
            paidAt: dateToApiString(paidAt),
            title: initialValues.title,
            amount: initialValues.amount ?? 0,
            currencyCode: initialValues.currencyCode,
            status: TransactionStatus.extracted,
            bookkeepingYearId: bookkeepingYear.id,
            bookingAccountId: initialValues.bookingAccountId,
            transactionReferenceId: null,
            userId: user.id,
            fileId,
            taxes: buildReceiptTaxes(
              initialValues.taxes,
              initialValues.amount ?? 0
            ),
          },
          selectedOrganization,
          selectedBookkeepingFull,
          baseCurrency
        );
        if (fx.rateMissing) missingRate += 1;
        created += 1;
        // Counters mutate only in synchronous blocks between awaits, so the
        // concurrent workers cannot interleave inside an update. No Saldo
        // update here: extracted rows never touch the balance — the full
        // amount posts when each receipt is verified.
        if (!earliestPaidAt || paidAt < earliestPaidAt) earliestPaidAt = paidAt;
      } catch {
        failed += 1;
      } finally {
        done += 1;
        toast.update(toastId, {
          title: `${done}/${files.length} Belege verarbeitet`,
          status: "info",
          duration: null,
        });
      }
    };
    // Capped worker pool. Each file is one Bedrock call and the model quota is
    // account-wide — the mobile app scans against the same one. A throttled
    // OCR fails silently into an unprefilled extracted row, so the cap stays
    // conservative; past ~3 the wall-clock gain flattens anyway (OCR dominates
    // per-file time). Raise the Bedrock service quota before raising this.
    const IMPORT_CONCURRENCY = 3;
    let cursor = 0;
    try {
      await Promise.all(
        Array.from(
          { length: Math.min(IMPORT_CONCURRENCY, files.length) },
          async () => {
            while (cursor < files.length) {
              const file = files[cursor];
              cursor += 1;
              await processFile(file);
            }
          }
        )
      );
    } finally {
      // Airtight: a rejecting worker (processFile catches everything, so only
      // the progress-toast update could throw) must not leave `importing`
      // stuck true — that would disable every upload path until reload.
      toast.close(toastId);
      setImporting(false);
    }
    toast({
      title: `${created} Belege importiert${
        failed > 0 ? `, ${failed} fehlgeschlagen` : ""
      }`,
      description:
        missingRate > 0
          ? `${missingRate} ohne Wechselkurs gespeichert — Beträge nicht in ${baseCurrency.toUpperCase()} umgerechnet.`
          : undefined,
      status: failed > 0 || missingRate > 0 ? "warning" : "success",
      // Sticky when rates are missing: this is the only signal that N receipts
      // carry no conversion, and an import is long enough to walk away from.
      ...(missingRate > 0 ? { duration: null, isClosable: true } : {}),
    });
    if (created > 0) {
      const widened = earliestPaidAt
        ? ensureDateRangeIncludes(earliestPaidAt)
        : false;
      setShowOnlyExtracted(true);
      if (!widened) refreshTransactions();
    }
  };

  const handleDroppedFiles = async (files: File[]) => {
    if (!selectedBookkeepingFull || !selectedOrganization || !user) return;
    if (!mostUsedBookingAccountId) {
      toast({
        title: "Keine Buchungskategorien vorhanden",
        status: "error",
      });
      return;
    }
    const supported = files.filter(isSupportedReceiptFile);
    const unsupportedFiles = files.filter((file) => !isSupportedReceiptFile(file));
    if (unsupportedFiles.length > 0) {
      // HEIC is the iPhone default and the one users hit by accident, so name it
      // and point somewhere actionable rather than the generic skip message.
      toast({
        title: unsupportedFiles.some(isHeicFile)
          ? "HEIC wird nicht unterstützt — bitte als JPG exportieren oder die App nutzen"
          : `${unsupportedFiles.length} Datei(en) übersprungen — Format nicht unterstützt`,
        status: "warning",
      });
    }
    if (supported.length === 0) return;
    if (supported.length === 1) {
      await processSingleFile(supported[0]);
    } else {
      await processMultipleFiles(supported);
    }
  };

  const openVerifyForTransaction = async (transaction: TransactionFull) => {
    const requestId = ++verifyRequestIdRef.current;
    setVerifyFileId(undefined);
    setVerifyDraftId(undefined);
    // Known before the fetch: the modal opens at full size right away instead
    // of flickering from the compact box to 6xl when the blob lands.
    setVerifyPreviewPending(transaction.attachments.length > 0);
    setVerifyTransaction(transaction);
    setVerifyInitialValues({
      paidAt: getLocalDate(new Date(transaction.paidAt)),
      title: transaction.title,
      amount: transaction.amount,
      currencyCode: transaction.currencyCode,
      // The stored account on an extracted row is a placeholder (most-used
      // default from multi-drop / abandoned drop), not a user choice — showing
      // it prefilled invites rubber-stamping. Same empty+required rule as the
      // single-drop form; the user must pick before Speichern enables.
      bookingAccountId:
        transaction.status === TransactionStatus.extracted
          ? undefined
          : transaction.bookingAccountId,
      transactionReferenceId: transaction.transactionReferenceId ?? undefined,
      taxes: transaction.taxes
        .filter((tax) => !tax.deletedAt)
        .map((tax) => ({
          taxRateId: tax.taxRateId,
          amount: tax.amount ?? null,
          totalAmount: tax.totalAmount ?? null,
        })),
    });
    setVerifyPreview(undefined);
    setVerifyLoading(false);
    setVerifyModalOpen(true);
    const attachmentKey = transaction.attachments[0]?.key;
    if (attachmentKey) {
      try {
        const receiptData = await fetchReceipt(attachmentKey);
        // Only attach if this open cycle is still the current one — otherwise
        // a slow fetch would show receipt A next to transaction B.
        if (receiptData?.blob && requestId === verifyRequestIdRef.current) {
          setVerifyPreview({
            url: URL.createObjectURL(receiptData.blob),
            isPdf: receiptData.key.toLowerCase().endsWith(".pdf"),
          });
        }
      } catch {
        if (requestId === verifyRequestIdRef.current) {
          toast({ title: "Beleg konnte nicht geladen werden", status: "warning" });
        }
      } finally {
        // Always drop the pending flag — a thrown fetch would otherwise park
        // the modal at full size with a spinner that never resolves.
        if (requestId === verifyRequestIdRef.current) {
          setVerifyPreviewPending(false);
        }
      }
    }
  };

  const closeVerifyModal = async () => {
    if (verifySavingRef.current) return;
    verifyRequestIdRef.current += 1;
    const abandonedFreshDrop =
      !verifyTransaction &&
      verifyFileId !== undefined &&
      verifyInitialValues !== undefined &&
      selectedBookkeepingFull &&
      selectedOrganization &&
      user;
    setVerifyModalOpen(false);
    if (verifyPreview) URL.revokeObjectURL(verifyPreview.url);
    setVerifyPreview(undefined);
    setVerifyPreviewPending(false);
    const fileId = verifyFileId;
    const draftId = verifyDraftId;
    const initialValues = verifyInitialValues;
    setVerifyFileId(undefined);
    setVerifyDraftId(undefined);
    setVerifyTransaction(undefined);
    setVerifyInitialValues(undefined);
    if (abandonedFreshDrop && fileId && initialValues) {
      // Keep the uploaded receipt as an extracted transaction (app inbox)
      // instead of dropping the work done so far.
      const paidAt = initialValues.paidAt ?? getLocalDate(new Date());
      const bookkeepingYear = getBookkeepingYear(selectedBookkeepingFull, paidAt);
      // The single-drop form seeds the booking account empty on purpose; the
      // row still needs one (bookingAccountId is non-null in the schema), so
      // the abandoned save falls back to the same most-used placeholder the
      // multi-drop path writes. Without it every abandoned drop would land in
      // the error branch below — misleading toast, orphaned S3 object.
      const bookingAccountId =
        initialValues.bookingAccountId ?? mostUsedBookingAccountId;
      if (!bookkeepingYear || !bookingAccountId) {
        // Returning silently here loses the upload: the modal is already torn
        // down, so the receipt becomes an orphaned S3 object with no row and no
        // feedback. Common with old receipts — a 2025 Beleg in a bookkeeping
        // that starts in 2026.
        toast({
          title: `Kein Buchhaltungsjahr für ${paidAt.getFullYear()} vorhanden`,
          description: "Der Beleg wurde nicht gespeichert.",
          status: "error",
        });
        return;
      }
      let fx: Fx;
      try {
        ({ fx } = await createReceiptTransaction(
          {
            paidAt: dateToApiString(paidAt),
            title: initialValues.title,
            amount: initialValues.amount ?? 0,
            currencyCode: initialValues.currencyCode,
            status: TransactionStatus.extracted,
            bookkeepingYearId: bookkeepingYear.id,
            bookingAccountId,
            transactionReferenceId: null,
            userId: user.id,
            fileId,
            taxes: buildReceiptTaxes(
              initialValues.taxes,
              initialValues.amount ?? 0
            ),
          },
          selectedOrganization,
          selectedBookkeepingFull,
          baseCurrency,
          // Same session id as a Speichern attempt: if a save committed but
          // its response was lost, this lands as an update of that row (seed
          // values, extracted) instead of a duplicate.
          draftId
        ));
      } catch {
        toast({ title: "Speichern fehlgeschlagen", status: "error" });
        return;
      }
      toast(
        fx.rateMissing
          ? missingRateToast(initialValues.currencyCode, baseCurrency)
          : {
              title: "Beleg als «Extrahiert» gespeichert",
              status: "info",
            }
      );
      // No Saldo update: extracted rows never touch the balance — same rule
      // that keeps them out of Bexio/Topal. The full amount posts at verify.
      if (!ensureDateRangeIncludes(paidAt)) refreshTransactions();
    }
  };

  const handleVerifySave = async (values: VerifyReceiptResult) => {
    if (!selectedBookkeepingFull || !selectedOrganization || !user) return;
    const bookkeepingYear = getBookkeepingYear(
      selectedBookkeepingFull,
      values.paidAt
    );
    if (!bookkeepingYear) {
      toast({
        title: `Kein Buchhaltungsjahr für ${values.paidAt.getUTCFullYear()} vorhanden`,
        status: "error",
      });
      return;
    }
    verifySavingRef.current = true;
    let fx: Fx;
    let balanceDelta: number;
    // Only the transaction write belongs in here. Everything after it runs on a
    // row that already exists, so a throw must not send us back to a state that
    // reads as "nothing saved yet" — that is what mints the second transaction.
    try {
      const signedNew = signedReceiptAmount(values.bookingAccountId, values.amount);
      if (verifyTransaction) {
        fx = await updateReceiptTransaction(
          verifyTransaction.id,
          buildReceiptInput(
            values,
            verifiedStatus,
            verifyTransaction.userId,
            bookkeepingYear.id
          ),
          selectedOrganization,
          selectedBookkeepingFull,
          baseCurrency,
          isFxCaptureStale(verifyTransaction, {
            currencyCode: values.currencyCode,
            amount: values.amount,
            paidAt: dateToApiString(values.paidAt),
          })
        );
        // Extracted rows never posted to the Saldo (inbox ≠ books, Michael's
        // ruling) — verifying posts the FULL amount, same as the fresh-create
        // branch. Any other status already posted at its last save, so an
        // edit posts only the delta, mirroring the app (globals.dart:658-668):
        // each side signed by its OWN booking account's taxType — the old sign
        // by the account the row was booked under, the new by the account
        // being saved — so an account change is captured in the delta.
        // The delta lands in the year of the NEW paidAt; the missing old-year
        // reversal on cross-year moves is the app's behavior too (86carztj2 —
        // mirror, don't fix here).
        // NOTE: extracted rows created before the never-post rule (the app
        // still posts on sendToInbox) were pre-posted; the release-bundle
        // migration un-posts them (86carutxk) — until then an app-created
        // extracted row verified here double-posts.
        const signedOld =
          verifyTransaction.bookingAccount.taxType === TaxType.earnings
            ? verifyTransaction.amount
            : -verifyTransaction.amount;
        balanceDelta =
          verifyTransaction.status === TransactionStatus.extracted
            ? signedNew
            : signedNew - signedOld;
      } else {
        ({ fx } = await createReceiptTransaction(
          {
            ...buildReceiptInput(values, verifiedStatus, user.id, bookkeepingYear.id),
            fileId: verifyFileId,
          },
          selectedOrganization,
          selectedBookkeepingFull,
          baseCurrency,
          // Stable across retries: a commit whose response was lost is
          // recognized on the next attempt instead of duplicated.
          verifyDraftId
        ));
        balanceDelta = signedNew;
      }
    } catch {
      // Nothing committed — the modal stays open so Speichern can be retried.
      toast({ title: "Speichern fehlgeschlagen", status: "error" });
      verifySavingRef.current = false;
      return;
    }
    // The row exists from here on. Tear the modal down before anything else can
    // throw: closeVerifyModal would read the still-set fileId as an abandoned
    // drop and re-save the row with its seed values.
    setVerifyFileId(undefined);
    setVerifyDraftId(undefined);
    setVerifyTransaction(undefined);
    setVerifyInitialValues(undefined);
    setVerifyModalOpen(false);
    setVerifyPreview((preview) => {
      if (preview) URL.revokeObjectURL(preview.url);
      return undefined;
    });
    verifySavingRef.current = false;
    toast(
      fx.rateMissing
        ? missingRateToast(values.currencyCode, baseCurrency)
        : { title: "Transaktion gespeichert", status: "success" }
    );
    // ÄT only, and it moves a number the transaction list is read against — so
    // it gets its own toast rather than riding on the save's.
    try {
      await applyReceiptBalanceUpdate(balanceDelta, values.paidAt);
    } catch {
      toast(balanceUpdateFailedToast);
    }
    if (!ensureDateRangeIncludes(values.paidAt)) refreshTransactions();
  };

  // --- End receipt dropzone flow ----------------------------------------

  const columns = useMemo(
    () => [
      ...(editing
        ? [
            columnHelper.display({
              id: "select",
              cell: (info) => (
                <Checkbox
                  colorScheme="primary"
                  isChecked={
                    !!transactionIdsSelected.find(
                      (tid) => info.row.original.id === tid
                    )
                  }
                  onChange={(event) => {
                    if (event.target.checked) {
                      setTransactionIdsSelected([
                        ...transactionIdsSelected,
                        info.row.original.id,
                      ]);
                    } else {
                      setTransactionIdsSelected(
                        transactionIdsSelected.filter(
                          (tid) => tid !== info.row.original.id
                        )
                      );
                    }
                  }}
                />
              ),
              header: () => (
                <Checkbox
                  colorScheme="primary"
                  onChange={(event) => {
                    if (event.target.checked) {
                      // Only what is actually on screen: `transactions` holds
                      // rows hidden by the search, the exported toggle, the
                      // «zu prüfen» filter and (for extracted) the date range.
                      // Selecting those and hitting delete would remove rows the
                      // user never saw.
                      setTransactionIdsSelected(
                        filteredTransactions?.map(
                          (transaction) => transaction.id
                        ) ?? []
                      );
                    } else {
                      setTransactionIdsSelected([]);
                    }
                  }}
                  isChecked={
                    (filteredTransactions?.length ?? 0) > 0 &&
                    transactionIdsSelected.length ===
                      filteredTransactions?.length
                  }
                />
              ),
            }),
          ]
        : []),
      columnHelper.accessor("paidAt", {
        // Extracted rows stay read-only in Bearbeiten mode (all cells): the
        // bulk save cannot edit their taxes, and on a Bexio bookkeeping it
        // flips every edited row to draft — an extracted row then syncs with
        // unusable taxes and lands on «Fehler». The verify modal (row click)
        // is their editor; the checkbox/delete still works.
        cell: (info) => {
          if (editing && info.row.original.status !== TransactionStatus.extracted) {
            const transactionUpdated = transactionsUpdated.find(
              (t) => t.id === info.row.original.id
            );

            return (
              <DateSelector
                defaultValue={getLocalDate(
                  new Date(transactionUpdated?.paidAt ?? info.getValue())
                )}
                onChange={(date) => {
                  if (date) {
                    if (transactionUpdated) {
                      setTransactionsUpdated(
                        transactionsUpdated.map((t) => ({
                          ...t,
                          paidAt:
                            t.id === info.row.original.id
                              ? dateToApiString(date)
                              : t.paidAt,
                        }))
                      );
                    } else {
                      setTransactionsUpdated([
                        ...transactionsUpdated,
                        {
                          ...info.row.original,
                          paidAt: dateToApiString(date),
                        },
                      ]);
                    }
                  }
                }}
                width="120px"
              />
            );
          } else {
            return formatDate(getLocalDate(new Date(info.getValue())));
          }
        },
        header: "Datum",
      }),
      columnHelper.accessor("title", {
        cell: (info) => {
          if (editing && info.row.original.status !== TransactionStatus.extracted) {
            const transactionUpdated = transactionsUpdated.find(
              (t) => t.id === info.row.original.id
            );

            return (
              <Input
                defaultValue={transactionUpdated?.title ?? info.getValue()}
                onBlur={(event) => {
                  if (transactionUpdated) {
                    setTransactionsUpdated(
                      transactionsUpdated.map((t) =>
                        t.id === info.row.original.id
                          ? { ...t, title: event.target.value }
                          : t
                      )
                    );
                  } else {
                    setTransactionsUpdated([
                      ...transactionsUpdated,
                      {
                        ...info.row.original,
                        title: event.target.value,
                      },
                    ]);
                  }
                }}
                width="200px"
              />
            );
          } else {
            return info.getValue();
          }
        },
        header: typeNameSingular,
      }),
      columnHelper.accessor("amount", {
        cell: (info) => {
          if (editing && info.row.original.status !== TransactionStatus.extracted) {
            const transactionUpdated = transactionsUpdated.find(
              (t) => t.id === info.row.original.id
            );

            const inputAmount = (
              <NumberInput
                defaultValue={transactionUpdated?.amount ?? info.getValue()}
                onBlur={(event) => {
                  if (transactionUpdated) {
                    setTransactionsUpdated(
                      transactionsUpdated.map((t) =>
                        t.id === info.row.original.id
                          ? { ...t, amount: parseFloat(event.target.value) }
                          : t
                      )
                    );
                  } else {
                    setTransactionsUpdated([
                      ...transactionsUpdated,
                      {
                        ...info.row.original,
                        amount: parseFloat(event.target.value),
                      },
                    ]);
                  }
                }}
                precision={2}
                width="100px"
              >
                <NumberInputField />
              </NumberInput>
            );

            const input =
              hasEarnings &&
              ((transactionUpdated &&
                selectedBookkeepingFull?.bookingAccounts.find(
                  (ba) => ba.id === transactionUpdated.bookingAccountId
                )?.taxType !== TaxType.earnings) ||
                (!transactionUpdated &&
                  info.row.original.bookingAccount.taxType !==
                    TaxType.earnings)) ? (
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <Box width="20px">-</Box>
                  </InputLeftElement>
                  {inputAmount}
                </InputGroup>
              ) : (
                inputAmount
              );

            return hasCurrencies ? (
              <HStack>
                <Select
                  value={optionsCurrencyCodesEffective.find(
                    (option) =>
                      option.value ===
                      (transactionUpdated?.currencyCode ??
                        info.row.original.currencyCode)
                  )}
                  options={optionsCurrencyCodesEffective}
                  onChange={(option) => {
                    // Reset captured FX on currency change — old
                    // exchangeRate/baseCurrencyAmount belong to the
                    // previous currency. Save flow recomputes.
                    if (transactionUpdated) {
                      setTransactionsUpdated(
                        transactionsUpdated.map((t) =>
                          t.id === info.row.original.id
                            ? {
                                ...t,
                                currencyCode: option?.value as CurrencyCode,
                                exchangeRate: null,
                                baseCurrencyAmount: null,
                              }
                            : t
                        )
                      );
                    } else {
                      setTransactionsUpdated([
                        ...transactionsUpdated,
                        {
                          ...info.row.original,
                          currencyCode: option?.value as CurrencyCode,
                          exchangeRate: null,
                          baseCurrencyAmount: null,
                        },
                      ]);
                    }
                  }}
                  width="80px"
                />
                {input}
              </HStack>
            ) : (
              input
            );
          } else {
            return formatAmount(
              transactionAmountSign(info.row.original) * info.getValue(),
              info.row.original.currencyCode
            );
          }
        },
        header: hasCurrencies ? "Original Betrag" : "Betrag",
      }),
      ...(hasCurrencies
        ? [
            columnHelper.accessor("baseCurrencyAmount", {
              cell: (info) => {
                const transactionUpdated = transactionsUpdated.find(
                  (t) => t.id === info.row.original.id
                );

                if (editing && transactionUpdated) {
                  return transactionUpdated.baseCurrencyAmount
                    ? formatAmount(
                        transactionAmountSign(transactionUpdated) *
                          (transactionUpdated.baseCurrencyAmount as number),
                        baseCurrency
                      )
                    : formatAmount(
                        transactionAmountSign(transactionUpdated) *
                          transactionUpdated.amount,
                        baseCurrency
                      );
                } else {
                  return info.getValue()
                    ? formatAmount(
                        transactionAmountSign(info.row.original) *
                          (info.getValue() as number),
                        baseCurrency
                      )
                    : formatAmount(
                        transactionAmountSign(info.row.original) *
                          info.row.original.amount,
                        baseCurrency
                      );
                }
              },
              header: `${baseCurrency.toUpperCase()} Betrag`,
            }),
          ]
        : []),
      ...(selectedBookkeepingFull?.taxRates &&
        selectedBookkeepingFull.taxRates.length > 0
        ? [
            columnHelper.accessor("taxes", {
              cell: (info) => {
                const transactionUpdated = transactionsUpdated.find(
                  (t) => t.id === info.row.original.id
                );
                const taxesActive = (
                  editing && transactionUpdated
                    ? transactionUpdated.taxes
                    : info.getValue()
                ).filter((tax) => !tax.deletedAt);

                if (editing && info.row.original.status !== TransactionStatus.extracted) {
                  return (
                    <HStack>
                      {taxesActive.length > 0 && (
                        <NumberInput
                          defaultValue={getTaxAmount(
                            transactionUpdated ?? info.row.original,
                            taxesActive[0] as Tax
                          )}
                          precision={2}
                          width="28"
                          isDisabled
                        >
                          <NumberInputField />
                        </NumberInput>
                      )}
                      {taxesActive.length > 1 && (
                        <NumberInput
                          defaultValue={getTaxAmount(
                            transactionUpdated ?? info.row.original,
                            taxesActive[1] as Tax
                          )}
                          precision={2}
                          width="28"
                          isDisabled
                        >
                          <NumberInputField />
                        </NumberInput>
                      )}
                    </HStack>
                  );
                } else {
                  return taxesActive
                    .map((tax) =>
                      formatAmount(
                        getTaxAmount(info.row.original, tax as Tax),
                        baseCurrency
                      )
                    )
                    .join(" | ");
                }
              },
              header: "MwSt. Betrag",
              id: "taxes_amount",
            }),
            columnHelper.accessor("taxes", {
              cell: (info) => {
                const taxesActive = info
                  .getValue()
                  .filter((tax) => !tax.deletedAt);

                if (editing && info.row.original.status !== TransactionStatus.extracted) {
                  const taxRatesActive =
                    selectedBookkeepingFull?.taxRates?.filter(
                      (taxRate) =>
                        !taxRate.deletedAt &&
                        new Date(taxRate.dateFrom) <=
                          new Date(info.row.original.paidAt) &&
                        (!taxRate.dateTo ||
                          new Date(taxRate.dateTo) >=
                            new Date(info.row.original.paidAt))
                    ) ?? [];
                  const optionsTaxRatesActive: SelectOption[] =
                    taxRatesActive.map((taxRate) => ({
                      value: taxRate.id,
                      label: `${taxRate.rate}%`,
                    }));

                  return (
                    <HStack>
                      {taxesActive.length > 0 && taxRatesActive.length > 0 && (
                        <Select
                          value={optionsTaxRatesActive.find(
                            (option) =>
                              option.value === taxesActive[0].taxRate.id
                          )}
                          options={optionsTaxRatesActive}
                          width="84px"
                          isDisabled
                        />
                      )}
                      {taxesActive.length > 1 && taxRatesActive.length > 0 && (
                        <Select
                          value={optionsTaxRatesActive.find(
                            (option) =>
                              option.value === taxesActive[1].taxRate.id
                          )}
                          options={optionsTaxRatesActive}
                          width="84px"
                          isDisabled
                        />
                      )}
                    </HStack>
                  );
                } else {
                  return taxesActive
                    .map((tax) => `${tax.taxRate?.rate}%`)
                    .join(" | ");
                }
              },
              header: "MwSt. Satz",
              id: "taxes_rate",
            }),
            columnHelper.accessor("taxes", {
              cell: (info) => {
                const taxesActive = info
                  .getValue()
                  .filter((tax) => !tax.deletedAt);

                return taxesActive
                  .map((tax) =>
                    formatAmount(
                      tax.totalAmount ?? info.row.original.amount,
                      baseCurrency
                    )
                  )
                  .join(" | ");
              },
              header: "MwSt. Betrag Total",
              id: "taxes_total_amount",
            }),
          ]
        : []),
      columnHelper.accessor("bookingAccount", {
        cell: (info) => {
          if (editing && info.row.original.status !== TransactionStatus.extracted) {
            const transactionUpdated = transactionsUpdated.find(
              (t) => t.id === info.row.original.id
            );

            return (
              <Select
                value={optionsBookingAccounts?.find(
                  (option) =>
                    option.value ===
                    (transactionUpdated?.bookingAccountId ?? info.getValue().id)
                )}
                options={optionsBookingAccounts}
                onChange={(option) => {
                  if (transactionUpdated) {
                    setTransactionsUpdated(
                      transactionsUpdated.map((t) =>
                        t.id === info.row.original.id
                          ? { ...t, bookingAccountId: option?.value as string }
                          : t
                      )
                    );
                  } else {
                    setTransactionsUpdated([
                      ...transactionsUpdated,
                      {
                        ...info.row.original,
                        bookingAccountId: option?.value as string,
                      },
                    ]);
                  }
                }}
                width="160px"
              />
            );
          } else if (
            info.row.original.status === TransactionStatus.extracted
          ) {
            // The stored account is only the most-used placeholder the schema
            // forces on insert — showing it would present a guess as a booking.
            // The row-click verify seeds it empty and the user picks there.
            return "–";
          } else {
            return (
              <Tag variant="outline">
                <TagLeftIcon
                  as={Image}
                  src={getTransactionCategoryIcon(
                    info.getValue().transactionCategory.icon
                  )}
                />
                <TagLabel>
                  {stringWithoutManualLineBreaks(info.getValue().name)}
                </TagLabel>
              </Tag>
            );
          }
        },
        header: "Buchungskategorie",
      }),
      ...(transactionReferences && transactionReferences.length > 0
        ? [
            columnHelper.accessor("transactionReference", {
              cell: (info) => {
                if (editing && info.row.original.status !== TransactionStatus.extracted) {
                  const transactionUpdated = transactionsUpdated.find(
                    (t) => t.id === info.row.original.id
                  );
                  const optionsTransactionReferences: SelectOption[] =
                    transactionReferences
                      .filter(
                        (transactionReference) =>
                          transactionReference.authUserId ===
                          info.row.original.userId
                      )
                      .map((transactionReference) => ({
                        value: transactionReference.id,
                        label: stringWithoutManualLineBreaks(
                          transactionReference.name
                        ),
                      }));

                  return (
                    <Select
                      value={optionsTransactionReferences.find(
                        (option) =>
                          option.value ===
                          (transactionUpdated?.transactionReferenceId ??
                            info.getValue()?.id)
                      )}
                      options={optionsTransactionReferences}
                      onChange={(option) => {
                        if (transactionUpdated) {
                          setTransactionsUpdated(
                            transactionsUpdated.map((t) =>
                              t.id === info.row.original.id
                                ? {
                                    ...t,
                                    transactionReferenceId:
                                      option?.value as string,
                                  }
                                : t
                            )
                          );
                        } else {
                          setTransactionsUpdated([
                            ...transactionsUpdated,
                            {
                              ...info.row.original,
                              transactionReferenceId: option?.value as string,
                            },
                          ]);
                        }
                      }}
                      width="160px"
                    />
                  );
                } else {
                  return info.getValue()?.name;
                }
              },
              header: "Referenz",
            }),
          ]
        : []),
      columnHelper.accessor("attachments", {
        cell: (info) => (
          <Tooltip label="Beleg herunterladen">
            <IconButton
              variant="outline"
              aria-label="Download receipt"
              icon={<Image boxSize="20px" src={IconFilePdf} />}
              isDisabled={info.getValue().length === 0}
              onClick={async (event) => {
                // The row itself opens the verify modal on extracted rows;
                // downloading the receipt must not also trigger that.
                event.stopPropagation();
                const key =
                  info.getValue().length > 0
                    ? info.getValue()[0].key
                    : undefined;

                if (key) {
                  const receiptData = await fetchReceipt(key);

                  if (receiptData && receiptData.blob) {
                    const url = URL.createObjectURL(receiptData.blob);
                    openInNewTab(url);
                  }
                }
              }}
            />
          </Tooltip>
        ),
        header: "Beleg",
      }),
      columnHelper.accessor("status", {
        cell: (info) => {
          const txn = info.row.original;
          return (
            <HStack spacing="2">
              <StatusChip type="sync" status={info.getValue()} />
              {txn.exportedAt && (
                <Tooltip
                  label={`Exportiert am ${format(
                    new Date(txn.exportedAt),
                    "dd.MM.yyyy HH:mm"
                  )}`}
                >
                  <span>
                    <StatusChip type="export" status="exported" />
                  </span>
                </Tooltip>
              )}
            </HStack>
          );
        },
        header: "Status",
      }),
      columnHelper.accessor("user", {
        cell: (info) => {
          const transactionUser = info.getValue();

          if (editing && info.row.original.status !== TransactionStatus.extracted) {
            return (
              <Select
                value={optionsUsers?.find(
                  (option) => option.value === transactionUser?.id
                )}
                options={optionsUsers}
                width="140px"
                isDisabled
              />
            );
          }

          return transactionUser ? getUserName(transactionUser) : "Unbekannt";
        },
        header: "Benutzer",
      }),
    ],
    [
      editing,
      hasCurrencies,
      hasEarnings,
      optionsBookingAccounts,
      optionsCurrencyCodesEffective,
      optionsUsers,
      selectedBookkeepingFull?.bookingAccounts,
      selectedBookkeepingFull?.taxRates,
      transactionAmountSign,
      transactionIdsSelected,
      transactionReferences,
      transactions,
      transactionsUpdated,
      baseCurrency,
    ]
  );

  const updateBalances = async (
    bookkeepingYearsAmount: Map<BookkeepingYear, number>
  ) => {
    // Update bookkeepingYear balance
    const bookkeepingYearsAmountPromises = Array.from(
      bookkeepingYearsAmount.entries()
    ).map(async ([bookkeepingYear, amount]) => {
      if (typeof bookkeepingYear.balance === "number") {
        await API.graphql<GraphQLQuery<UpdateBookkeepingYearMutation>>({
          query: updateBookkeepingYear,
          variables: {
            input: {
              id: bookkeepingYear.id,
              balance: bookkeepingYear.balance + amount,
            },
          },
        });
      }
    });

    await Promise.all(bookkeepingYearsAmountPromises);

    // Trigger Bookkeeping update
    setSelectedBookkeepingFull(undefined);
  };

  useEffect(() => {
    if (selectedBookkeepingFull) {
      setHasEarnings(
        selectedBookkeepingFull.bookingAccounts.some(
          (bookingAccount) => bookingAccount.taxType === TaxType.earnings
        )
      );
    }
  }, [selectedBookkeepingFull]);

  useEffect(() => {
    if (selectedOrganization) {
      setHasCurrencies(selectedOrganization.logo !== aerzteTreuhandLogo);
    }
  }, [selectedOrganization]);

  // Accountant defaults: show all users (incl. deleted) + full year date range (only on initial load)
  const accountantDefaultsApplied = useRef(false);
  useEffect(() => {
    if (!accountantDefaultsApplied.current && user?.role === UserAppRole.accountant) {
      accountantDefaultsApplied.current = true;
      setIncludeDeletedUsers(true);
      const today = new Date();
      setDateRange({
        from: getLocalDate(startOfYear(today)),
        to: getLocalDate(today),
      });
    }
  }, [user?.role, setIncludeDeletedUsers]);

  useEffect(() => {
    // Consume-once: run only when a fetch was actually requested (nonce moved),
    // not whenever some other dependency changes. Two bumps in one commit fold
    // into a single run with the latest values — that is the point.
    if (fetchNonce !== processedFetchNonceRef.current) {
      processedFetchNonceRef.current = fetchNonce;
      if (
        selectedBookkeepingFull &&
        selectedBookkeepingFull.organizationId === selectedOrganization?.id
      ) {
        async function fetchTransactions() {
          if (dateRange?.from && dateRange?.to) {
            const years = [];

            // Clone before mutating — dateRange.to is the same Date object
            // stored in React state; mutating it would shift the boundary on
            // subsequent renders and break date-range comparisons.
            const dateRangeTo = new Date(dateRange.to);
            dateRangeTo.setHours(23, 59, 59, 999);

            for (
              let i = dateRange.from.getFullYear();
              i <= dateRangeTo.getFullYear();
              i++
            ) {
              years.push(i);
            }

            const bookkeepingYearsInRange = years
              .map((year) =>
                getBookkeepingYear(
                  selectedBookkeepingFull as BookkeepingFull,
                  new Date(year, 2, 2)
                )
              )
              .filter((bookkeepingYear) => bookkeepingYear);

            // Extracted receipts are an inbox, not a date-range view — the app's
            // inbox query carries no date predicate at all (transactions_list
            // .dart:365). A receipt dated outside the current window would
            // otherwise be fetched once, then vanish on reload and become
            // unreachable. So query every year and OR the status into the filter
            // below, keeping them visible whatever the range says.
            const bookkeepingYears =
              (selectedBookkeepingFull as BookkeepingFull).bookkeepingYears
                ?.length
                ? (selectedBookkeepingFull as BookkeepingFull).bookkeepingYears
                : bookkeepingYearsInRange;

            if (
              bookkeepingYears.length > 0 &&
              selectedUsers &&
              selectedUsers.length > 0
            ) {
              const transactionPromises = bookkeepingYears.map(
                async (bookkeepingYear) =>
                  // Custom document: also selects bill { id status } for the
                  // row-edit gate, and tolerates the nested-bill auth
                  // redaction heads get on other users' rows.
                  await listTransactionsWithBill({
                    bookkeepingYearId: bookkeepingYear!.id,
                    filter: {
                      and: ([
                        {
                          // In the window OR awaiting verification: extracted
                          // rows must stay reachable regardless of the range
                          // (app inbox has no date predicate).
                          or: [
                            {
                              paidAt: {
                                between: [
                                  dateToApiString(dateRange.from!),
                                  dateToApiString(dateRangeTo),
                                ],
                              },
                            },
                            { status: { eq: TransactionStatus.extracted } },
                          ],
                        },
                        // Skip user filter if all users selected
                        allUsersSelected
                          ? undefined
                          : selectedUsers?.length > 1
                            ? {
                                or: selectedUsers.map((user) => ({
                                  userId: {
                                    eq: user.value,
                                  },
                                })),
                              }
                            : {
                                userId: {
                                  eq: selectedUsers[0].value,
                                },
                              },
                        // Skip booking account filter if all accounts selected
                        allBookingAccountsSelected
                          ? undefined
                          : selectedBookingAccounts && selectedBookingAccounts.length > 0
                            ? selectedBookingAccounts.length > 1
                              ? {
                                  or: selectedBookingAccounts.map((account) => ({
                                    bookingAccountId: { eq: account.value },
                                  })),
                                }
                              : {
                                  bookingAccountId: {
                                    eq: selectedBookingAccounts[0].value,
                                  },
                                }
                            : undefined,
                        // Skip transaction reference filter if all selected
                        // Values are names (deduplicated across users), resolve to all matching IDs
                        // NO_REFERENCE sentinel matches transactions with null transactionReferenceId
                        allTransactionReferencesSelected
                          ? undefined
                          : selectedTransactionReferences && selectedTransactionReferences.length > 0
                            ? (() => {
                                const selectedNames = new Set(selectedTransactionReferences.map((r) => r.value));
                                const includeNoReference = selectedNames.has(NO_REFERENCE);
                                selectedNames.delete(NO_REFERENCE);
                                const matchingIds = transactionReferences
                                  ?.filter((r) => !r.deletedAt && selectedNames.has(stringWithoutManualLineBreaks(r.name)))
                                  .map((r) => r.id) ?? [];
                                const conditions = [
                                  ...matchingIds.map((id) => ({
                                    transactionReferenceId: { eq: id },
                                  })),
                                  ...(includeNoReference
                                    ? [
                                        { transactionReferenceId: { attributeExists: false } },
                                        { transactionReferenceId: { eq: null } },
                                      ]
                                    : []),
                                ];
                                return conditions.length > 1
                                  ? { or: conditions }
                                  : conditions.length === 1
                                    ? conditions[0]
                                    : undefined;
                              })()
                            : undefined,
                      ] as Array<ModelTransactionFilterInput | undefined>).filter(
                        (filter): filter is ModelTransactionFilterInput =>
                          filter !== undefined
                      ),
                    },
                  })
              );

              const transactionsByYear = await Promise.all(transactionPromises);

              const transactionFullPromises = transactionsByYear
                .flat()
                .map(async (transaction) => {
                  const bookingAccount =
                    selectedBookkeepingFull?.bookingAccounts.find(
                      (bookingAccount) =>
                        bookingAccount.id === transaction?.bookingAccountId
                    ) as BookingAccount;
                  const transactionCategory = transactionCategories?.find(
                    (transactionCategory) =>
                      transactionCategory.id ===
                      bookingAccount.transactionCategoryId
                  ) as TransactionCategory;
                  const transactionReference = transactionReferences?.find(
                    (transactionReference) =>
                      transactionReference.id ===
                      transaction?.transactionReferenceId
                  );
                  const bookingAccountFull = {
                    ...bookingAccount,
                    transactionCategory: transactionCategory,
                  };
                  const user = users?.find(
                    (user) => user.id === transaction.userId
                  ) as User;
                  const taxes = await queryList<
                    Tax,
                    TaxesByTransactionIdQueryVariables,
                    TaxesByTransactionIdQuery
                  >(taxesByTransactionId, {
                    transactionId: transaction.id,
                  });
                  const attachments = await queryList<
                    Attachment,
                    AttachmentsByTransactionIdQueryVariables,
                    AttachmentsByTransactionIdQuery
                  >(attachmentsByTransactionId, {
                    transactionId: transaction.id,
                  });

                  return {
                    ...(transaction as unknown as Transaction),
                    bookingAccount: bookingAccountFull,
                    taxes: taxes.map((tax) => ({
                      ...tax,
                      taxRate: selectedBookkeepingFull?.taxRates?.find(
                        (taxRate) => taxRate.id === tax.taxRateId
                      ) as TaxRate,
                    })),
                    user: user,
                    attachments: attachments,
                    transactionReference: transactionReference,
                  };
                });

              const transactionsAll: TransactionFull[] = await Promise.all(
                transactionFullPromises
              );

              setTransactions(
                transactionsAll.sort((a, b) =>
                  compareStrings(b.paidAt, a.paidAt)
                )
              );

              // Reset editing states. Read via refs so the effect does NOT
              // depend on transactionIdsSelected / transactionsUpdated (which
              // would re-trigger this fetch on every checkbox toggle).
              setTransactionIdsSelected(
                transactionIdsSelectedRef.current.filter((tid) =>
                  transactionsAll.find((t) => t.id === tid)
                )
              );
              setTransactionsUpdated(
                transactionsUpdatedRef.current.filter((t) =>
                  transactionsAll.find((transaction) => transaction.id === t.id)
                )
              );
            } else {
              setTransactions([]);
            }
          }
        }

        fetchTransactions();
      } else {
        setTransactions([]);
      }

      setState("success");
    }
  }, [
    allBookingAccountsSelected,
    allTransactionReferencesSelected,
    allUsersSelected,
    dateRange,
    selectedBookingAccounts,
    selectedBookkeepingFull,
    selectedOrganization,
    selectedTransactionReferences,
    selectedUsers,
    fetchNonce,
    transactionCategories,
    transactionReferences,
    users,
  ]);

  useEffect(() => {
    // Clear transactions if no users OR if booking accounts explicitly empty (not undefined/loading)
    if (
      !selectedUsers ||
      selectedUsers.length === 0 ||
      (selectedBookingAccounts && selectedBookingAccounts.length === 0) ||
      (selectedTransactionReferences && selectedTransactionReferences.length === 0)
    ) {
      setTransactions([]);
      setTransactionIdsSelected([]);
      setTransactionsUpdated([]);
      setState("success");
    } else {
      setState("loading");
      setFetchNonce((nonce) => nonce + 1);
    }
  }, [
    dateRange,
    selectedBookingAccounts,
    selectedBookkeepingFull,
    selectedOrganization,
    selectedTransactionReferences,
    selectedUsers,
    transactionReferences,
  ]);

  // User selection: accountants see all users, others see only themselves
  // Track which optionsUsers we've applied to detect when users change (new org or refresh)
  const lastAppliedOptionsUsers = useRef<MultiSelectOption[] | undefined>(
    undefined
  );
  useEffect(() => {
    if (users && users.length > 0 && selectedOrganization && optionsUsers) {
      // Only apply if optionsUsers has changed (new org or users refreshed)
      if (lastAppliedOptionsUsers.current !== optionsUsers) {
        if (user?.role === UserAppRole.accountant) {
          // Accountant: select all users
          setSelectedUsers(optionsUsers);
          lastAppliedOptionsUsers.current = optionsUsers;
        } else {
          // Non-accountant: select current user only
          const defaultUser = users.find((u) => u.id === user?.id) ?? users[0];
          setSelectedUsers([
            { label: getUserName(defaultUser), value: defaultUser.id },
          ]);
          lastAppliedOptionsUsers.current = optionsUsers;
        }
      }
    }
  }, [users, user?.id, user?.role, selectedOrganization, optionsUsers]);

  useEffect(() => {
    if (optionsBookingAccounts && optionsBookingAccounts.length > 0) {
      setSelectedBookingAccounts(optionsBookingAccounts);
    }
  }, [optionsBookingAccounts]);

  useEffect(() => {
    if (optionsTransactionReferences && optionsTransactionReferences.length > 1) {
      setSelectedTransactionReferences(optionsTransactionReferences);
    }
  }, [optionsTransactionReferences]);

  const hasExportedItems = useMemo(
    () => (transactions ?? []).some((t) => t.exportedAt),
    [transactions]
  );

  // Filter transactions by search query and exportedAt toggle
  const filteredTransactions = useMemo(() => {
    let base = transactions;
    if (!showExported) {
      base = (base ?? []).filter((t) => !t.exportedAt);
    }
    if (showOnlyExtractedEffective) {
      // Inbox view: every extracted receipt, whatever the date range says.
      base = (base ?? []).filter(
        (t) => t.status === TransactionStatus.extracted
      );
    } else if (dateRange?.from && dateRange?.to) {
      // The fetch deliberately over-fetches extracted rows from outside the
      // window (they must stay reachable — see the inbox comment in the load
      // effect). Re-apply the window here so the normal view and its Total keep
      // meaning "the selected period". AWSDate strings compare lexicographically,
      // which avoids the UTC-vs-local midnight trap.
      const from = dateToApiString(dateRange.from);
      const to = dateToApiString(dateRange.to);
      base = (base ?? []).filter((t) => t.paidAt >= from && t.paidAt <= to);
    }
    if (!base || !searchQuery.trim()) return base;

    const query = searchQuery.toLowerCase().trim();
    if (query.length < 2) return base;

    return base.filter((t) => {
      // Text fields - fuzzy match with CONTAINS threshold
      const taxRateCodes = t.taxes?.map((tax) => tax.taxRate?.code) ?? [];
      const textFields = [
        t.title,
        getUserName(t.user),
        t.bookingAccount?.name,
        t.bookingAccount?.transactionCategory?.name,
        ...taxRateCodes,
      ].filter(Boolean) as string[];

      const hasTextMatch = textFields.some(
        (field) => rankItem(field, query, { threshold: rankings.CONTAINS }).passed
      );
      if (hasTextMatch) return true;

      // Structured fields - exact substring match
      const formattedAmount = t.amount.toFixed(2);
      const formattedBaseCurrencyAmount = t.baseCurrencyAmount?.toFixed(2);
      const formattedDate = formatDate(new Date(t.paidAt));
      const taxRates = t.taxes?.map((tax) => tax.taxRate?.rate?.toString()) ?? [];
      const taxAmounts = t.taxes?.flatMap((tax) => [
        tax.amount?.toFixed(2),
        tax.totalAmount?.toFixed(2),
      ]) ?? [];

      return (
        formattedAmount.includes(query) ||
        formattedBaseCurrencyAmount?.includes(query) ||
        formattedDate.toLowerCase().includes(query) ||
        taxRates.some((rate) => rate?.includes(query)) ||
        taxAmounts.some((amount) => amount?.includes(query)) ||
        t.currencyCode?.toLowerCase().includes(query) ||
        t.transactionReference?.name?.toLowerCase().includes(query)
      );
    });
  }, [
    transactions,
    searchQuery,
    showExported,
    showOnlyExtractedEffective,
    dateRange,
  ]);

  // Calculate totals for footer
  const totals = useMemo(() => {
    if (!filteredTransactions || filteredTransactions.length === 0) return null;

    let totalAmount = 0;
    let totalBaseCurrencyAmount = 0;
    let totalTaxAmount = 0;

    for (const t of filteredTransactions) {
      const sign = transactionAmountSign(t);
      totalAmount += sign * t.amount;
      totalBaseCurrencyAmount += sign * (t.baseCurrencyAmount ?? t.amount);

      // Sum tax amounts
      const activeTaxes = t.taxes?.filter((tax) => !tax.deletedAt) ?? [];
      for (const tax of activeTaxes) {
        totalTaxAmount += getTaxAmount(t, tax);
      }
    }

    return { totalAmount, totalBaseCurrencyAmount, totalTaxAmount };
  }, [filteredTransactions, transactionAmountSign]);

  // Footer row for totals
  const footerRow = useMemo(() => {
    if (!totals) return null;

    return (
      <Tr>
        {editing && <Td />}
        <Td fontWeight="semibold">Total</Td>
        <Td />
        <Td fontWeight="semibold">
          {hasCurrencies ? "" : formatAmount(totals.totalAmount, baseCurrency)}
        </Td>
        {hasCurrencies && (
          <Td fontWeight="semibold">
            {formatAmount(totals.totalBaseCurrencyAmount, baseCurrency)}
          </Td>
        )}
        {selectedBookkeepingFull?.taxRates &&
          selectedBookkeepingFull.taxRates.length > 0 && (
            <>
              <Td fontWeight="semibold">
                {formatAmount(totals.totalTaxAmount, baseCurrency)}
              </Td>
              <Td />
              <Td />
            </>
          )}
        <Td />
        {transactionReferences && transactionReferences.length > 0 && <Td />}
        <Td />
        <Td />
        <Td />
      </Tr>
    );
  }, [
    totals,
    editing,
    hasCurrencies,
    selectedBookkeepingFull?.taxRates,
    transactionReferences,
    baseCurrency,
  ]);

  const balanceData = useMemo(() => {
    if (
      selectedOrganization?.logo === aerzteTreuhandLogo &&
      selectedBookkeepingFull &&
      dateRange?.to
    ) {
      const year = dateRange.to.getFullYear();
      const bookkeepingYear = getBookkeepingYear(
        selectedBookkeepingFull,
        dateRange.to
      );
      if (typeof bookkeepingYear?.balance === "number") {
        return { balance: bookkeepingYear.balance, year };
      }
    }
    return undefined;
  }, [selectedOrganization?.logo, selectedBookkeepingFull, dateRange?.to]);

  type BillingModalProps = {
    pretrialActive: boolean;
    organizationAdminUser: User | undefined;
    onClose?: () => void;
    closed?: boolean;
  };

  // Named: the dropzone guard reads this, and `!isOpen` there would be unreadable.
  const { isOpen: billingModalOpen, onOpen, onClose } = useDisclosure();
  const buttonRef = useRef(null);
  const [billingModalProps, setBillingModalProps] = useState<BillingModalProps>(
    {
      pretrialActive: true,
      organizationAdminUser: undefined,
    }
  );

  useEffect(() => {
    if (
      selectedOrganization &&
      user &&
      showBillingModal(selectedOrganization, user) &&
      !billingModalOpen &&
      !billingModalProps.closed
    ) {
      const showBillingModal = async () => {
        const _pretrialActive = isPretrialActive(selectedOrganization);
        const usersResponse = await queryList<
          User,
          ListUsersQueryVariables,
          ListUsersQuery
        >(listUsers, {
          filter: {
            role: { eq: UserAppRole.organizationAdmin },
          },
        });

        setBillingModalProps({
          pretrialActive: _pretrialActive,
          organizationAdminUser:
            usersResponse.length > 0 ? usersResponse[0] : undefined,
          onClose: _pretrialActive
            ? () => {
                setBillingModalProps({ ...billingModalProps, closed: true });
              }
            : undefined,
          closed: false,
        });
      };
      showBillingModal();
    }
  }, [billingModalProps, billingModalOpen, selectedOrganization, user]);

  useEffect(() => {
    if (billingModalProps.closed !== undefined) {
      if (billingModalProps.closed) {
        onClose();
      } else {
        onOpen();
      }
    }
  }, [billingModalProps.closed, onClose, onOpen]);

  // Billing gates on the synchronous predicate, not on the dialog being open:
  // the dialog only appears after an async admin-user fetch, and gating on
  // `billingModalOpen` left uploads live during that fetch — or forever, if it
  // rejects. Fail closed instead; the explicit pretrial dismissal is the one
  // state that re-enables.
  const billingBlocksUploads =
    !!selectedOrganization &&
    !!user &&
    showBillingModal(selectedOrganization, user) &&
    billingModalProps.closed !== true;

  // One gate for both upload entry points — the drop overlay and the toolbar
  // button. The billing gate matters most: past the pretrial window that
  // dialog has no close button, no overlay click and no Escape — so nothing
  // else here would stop an upload. Letting one through would run OCR and S3
  // puts for an org that stopped paying, and there is no server-side backstop
  // (no billing-based @auth).
  const uploadEnabled =
    !!selectedBookkeepingFull &&
    !!selectedOrganization &&
    !!user &&
    !verifyModalOpen &&
    !billingBlocksUploads &&
    !editing &&
    !importing;

  return (
    <VStack p="4" alignItems="start" width="full">
      <AlertDialog
        isOpen={billingModalOpen}
        leastDestructiveRef={buttonRef}
        onClose={billingModalProps.onClose ?? emptyFunction}
        isCentered
        closeOnOverlayClick={billingModalProps.pretrialActive}
      >
        <AlertDialogOverlay>
          <AlertDialogContent m={4}>
            <AlertDialogHeader
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              pb={0}
            >
              <Text fontSize="lg" fontWeight="semibold">
                Zahlungsdetails aktualisieren
              </Text>
              {billingModalProps.onClose && (
                <IconButton
                  variant="ghost"
                  aria-label="Close"
                  icon={
                    <Flex
                      boxSize="20px"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <IconCancel stroke={gray700} />
                    </Flex>
                  }
                  onClick={billingModalProps.onClose}
                  isDisabled={billingModalProps.onClose === undefined}
                />
              )}
            </AlertDialogHeader>
            <AlertDialogBody color="gray.600">
              {user?.role === UserAppRole.organizationAdmin
                ? `Um Expensly${
                    billingModalProps.pretrialActive ? " in Zukunft" : ""
                  } weiterhin nutzen zu können, musst du die Zahlungsdetails aktualisieren.`
                : "Um Expensly weiterhin nutzen zu können, muss dein Organisationsleiter die Zahlungsdetails auf web.expensly.ch aktualisieren."}
              {user?.role !== UserAppRole.organizationAdmin &&
                billingModalProps.organizationAdminUser && (
                  <>
                    <br />
                    {`Kontaktiere dazu ${billingModalProps.organizationAdminUser.email}`}
                  </>
                )}
              {user?.role !== UserAppRole.organizationAdmin && (
                <>
                  <br />
                  <br />
                  Klicke auf Bestätigen, wenn die Zahlungsdetails aktualisiert
                  wurden.
                </>
              )}
            </AlertDialogBody>
            <AlertDialogFooter flexDirection="column">
              <Button
                ref={buttonRef}
                colorScheme="primary"
                onClick={
                  user?.role === UserAppRole.organizationAdmin
                    ? () => navigate(routeOnboardingBilling)
                    : billingModalProps.onClose ?? reloadWindow
                }
                width="full"
              >
                {user?.role === UserAppRole.organizationAdmin
                  ? "Zahlungsdetails aktualisieren"
                  : "Bestätigen"}
              </Button>
              {!billingModalProps.onClose && (
                <SignOutLinkButton mt="12px" withName={false} />
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      <Flex
        mb="4"
        alignItems={{ base: "stretch", md: "center" }}
        gap={{ base: "2", md: "4" }}
        width="full"
        zIndex={2}
        direction={{ base: "column", md: "row" }}
      >
        <Heading size="md">{typeNamePlural}</Heading>
        <Spacer />
        <Input
          maxW={{ base: "full", md: "200px" }}
          placeholder="Suchen..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          bg="white"
        />
        <DateRangeSelector
          range={dateRange}
          setRange={setDateRange}
          state={state}
        />
        <MultiSelect
          options={optionsBookingAccounts}
          value={selectedBookingAccounts}
          onChange={setSelectedBookingAccounts}
          onAllSelectedChange={setAllBookingAccountsSelected}
          placeholder="Kategorien..."
          allLabel="Alle Buchungskategorien"
        />
        {optionsTransactionReferences && optionsTransactionReferences.length > 1 && (
          <MultiSelect
            options={optionsTransactionReferences}
            value={selectedTransactionReferences}
            onChange={setSelectedTransactionReferences}
            onAllSelectedChange={setAllTransactionReferencesSelected}
            placeholder="Referenzen..."
            allLabel="Alle Referenzen"
          />
        )}
        <MultiSelect
          options={optionsUsers}
          value={selectedUsers}
          onChange={setSelectedUsers}
          onAllSelectedChange={setAllUsersSelected}
          allLabel="Alle Benutzer"
        />
        {user?.role !== UserAppRole.user && (
          <Tooltip label="Gelöschte Benutzer einbeziehen">
            <IconButton
              variant="outline"
              backgroundColor={includeDeletedUsers ? "gray.100" : undefined}
              aria-label="Include deleted users"
              icon={<Image boxSize="20px" src={IconUsersX} />}
              onClick={() => setIncludeDeletedUsers(!includeDeletedUsers)}
            />
          </Tooltip>
        )}
        {extractedCount > 0 && (
          <Tooltip label="Nur Belege mit Status «Extrahiert» anzeigen">
            <Button
              variant={showOnlyExtractedEffective ? "solid" : "outline"}
              colorScheme="warning"
              onClick={() => setShowOnlyExtracted(!showOnlyExtractedEffective)}
              flexShrink={0}
            >
              {extractedCount} zu prüfen
            </Button>
          </Tooltip>
        )}
        <ShowExportedToggle
          showExported={showExported}
          setShowExported={setShowExported}
          hasExportedItems={hasExportedItems}
        />
        <Flex gap={{ base: "2", md: "4" }}>
          {/* Same formats as isSupportedReceiptFile — the picker filters, the
              pipeline still validates. */}
          <Input
            ref={uploadInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp,.gif"
            multiple
            display="none"
            data-testid="upload-input"
            onChange={(event) => {
              const files = Array.from(event.target.files ?? []);
              // Reset so picking the same file again still fires a change.
              event.target.value = "";
              if (files.length > 0) handleDroppedFiles(files);
            }}
          />
          <Tooltip label="Auch per Drag & Drop möglich">
            <Button
              colorScheme="primary"
              leftIcon={<AddIcon boxSize="10px" />}
              flexShrink={0}
              isDisabled={!uploadEnabled}
              onClick={() => uploadInputRef.current?.click()}
            >
              Hochladen
            </Button>
          </Tooltip>
          <DownloadButton
            bookkeeping={selectedBookkeepingFull}
            transactions={filteredTransactions?.filter((t) =>
              transactionIdsSelected.length > 0
                ? transactionIdsSelected.includes(t.id)
                : true
            )}
            baseCurrency={baseCurrency}
            state={state}
            dateFrom={dateRange?.from}
            dateTo={dateRange?.to}
            hasTaxes={
              selectedBookkeepingFull?.taxRates &&
              selectedBookkeepingFull.taxRates.length > 0
            }
            organizationName={selectedOrganization?.name}
            onExportComplete={() => {
              refreshTransactions();
            }}
          />
          {transactionIdsSelected.length > 0 && (
            <DeleteButton
              // Carries the count: the dialog is the last thing between a
              // selection and a soft delete, and "Transaktionen wirklich
              // löschen?" never said how many.
              typeName={
                transactionIdsSelected.length === 1
                  ? typeNameSingular
                  : `${transactionIdsSelected.length} ${typeNamePlural}`
              }
              onDelete={async () => {
                setDeleting(true);

                const transactionDeletePromises = transactionIdsSelected.map(
                  async (transactionId) => {
                    await deleteItem(
                      updateTransaction,
                      transactionId,
                      selectedBookkeepingFull?.accountingSystem ===
                        AccountingSystem.bexio
                        ? {
                            status: TransactionStatus.draft,
                          }
                        : undefined
                    );
                  }
                );

                await Promise.all(transactionDeletePromises);

                // Update balance if ÄT
                if (
                  selectedOrganization?.logo === aerzteTreuhandLogo &&
                  selectedBookkeepingFull
                ) {
                  const bookkeepingYearsAmount = new Map<
                    BookkeepingYear,
                    number
                  >();
                  for (const transactionId of transactionIdsSelected) {
                    const transaction = transactions?.find(
                      (t) => t.id === transactionId
                    );
                    // Extracted rows never posted to the Saldo — reversing
                    // them here would subtract an amount that was never added.
                    if (
                      transaction &&
                      transaction.status !== TransactionStatus.extracted
                    ) {
                      const bookkeepingYear = getBookkeepingYear(
                        selectedBookkeepingFull,
                        getLocalDate(new Date(transaction.paidAt))
                      );
                      if (bookkeepingYear) {
                        const amount =
                          transaction.bookingAccount.taxType ===
                          TaxType.earnings
                            ? -transaction.amount
                            : transaction.amount;

                        if (bookkeepingYearsAmount.has(bookkeepingYear)) {
                          bookkeepingYearsAmount.set(
                            bookkeepingYear,
                            bookkeepingYearsAmount.get(bookkeepingYear)! +
                              amount
                          );
                        } else {
                          bookkeepingYearsAmount.set(bookkeepingYear, amount);
                        }

                        // Also update next bookkeepingYear if transaction was from a previous year
                        const nextBookkeepingYear = getBookkeepingYear(
                          selectedBookkeepingFull,
                          getLocalDate(new Date())
                        );
                        if (
                          nextBookkeepingYear &&
                          nextBookkeepingYear.year > bookkeepingYear?.year
                        ) {
                          if (bookkeepingYearsAmount.has(nextBookkeepingYear)) {
                            bookkeepingYearsAmount.set(
                              nextBookkeepingYear,
                              bookkeepingYearsAmount.get(nextBookkeepingYear)! +
                                amount
                            );
                          } else {
                            bookkeepingYearsAmount.set(
                              nextBookkeepingYear,
                              amount
                            );
                          }
                        }
                      }
                    }
                  }

                  await updateBalances(bookkeepingYearsAmount);
                }

                setDeleting(false);
                setEditing(false);
                refreshTransactions();
              }}
              isLoading={deleting}
            />
          )}
          {editing ? (
            <Button
              variant="outline"
              colorScheme="error"
              leftIcon={
                <Flex boxSize="20px" alignItems="center">
                  <IconCancel stroke={error700} />
                </Flex>
              }
              onClick={() => {
                setEditing(false);
              }}
            >
              Abbrechen
            </Button>
          ) : (
            <Tooltip label="Bearbeiten">
              <IconButton
                variant="outline"
                aria-label="Edit"
                icon={<IconEdit stroke={gray700} />}
                onClick={() => setEditing(true)}
                isDisabled={state !== "success" || transactions?.length === 0}
              />
            </Tooltip>
          )}
          {editing && (
            <Button
              variant="outline"
              colorScheme="success"
              leftIcon={
                <Flex boxSize="20px" alignItems="center">
                  <IconEdit stroke={success700} />
                </Flex>
              }
              isLoading={updating}
              onClick={async () => {
                setUpdating(true);

                // Belt to the read-only cells: an extracted row must never go
                // through this save — on Bexio it would flip to draft and sync
                // with unusable taxes («Fehler»).
                const transactionUpdatePromises = transactionsUpdated
                  .filter(
                    (transactionUpdated) =>
                      transactionUpdated.status !== TransactionStatus.extracted
                  )
                  .map(async (transactionUpdated) => {
                    const transactionOriginal = transactions?.find(
                      (t) => t.id === transactionUpdated.id
                    );
                    await API.graphql<GraphQLQuery<UpdateTransactionMutation>>({
                      query: updateTransaction,
                      variables: {
                        input: {
                          id: transactionUpdated.id,
                          ...(transactionOriginal?.paidAt !==
                          transactionUpdated.paidAt
                            ? {
                                paidAt: transactionUpdated.paidAt,
                                // Also update bookkeepingYearId
                                // if paidAt date has changed
                                bookkeepingYearId: getBookkeepingYear(
                                  selectedBookkeepingFull!,
                                  getLocalDate(
                                    new Date(transactionUpdated.paidAt)
                                  )
                                )?.id,
                              }
                            : {}),
                          ...(transactionOriginal?.title !==
                          transactionUpdated.title
                            ? {
                                title: transactionUpdated.title,
                              }
                            : {}),
                          ...(transactionOriginal?.amount !==
                          transactionUpdated.amount
                            ? {
                                amount: transactionUpdated.amount,
                              }
                            : {}),
                          ...(transactionOriginal?.currencyCode !==
                          transactionUpdated.currencyCode
                            ? {
                                currencyCode: transactionUpdated.currencyCode,
                                // Captured FX belonged to the previous
                                // currency; clear so downstream readers
                                // (CSV export, Bexio sync) don't apply
                                // a stale rate.
                                exchangeRate: null,
                                baseCurrencyAmount: null,
                              }
                            : {}),
                          ...(transactionOriginal?.bookingAccountId !==
                          transactionUpdated.bookingAccountId
                            ? {
                                bookingAccountId:
                                  transactionUpdated.bookingAccountId,
                              }
                            : {}),
                          ...(transactionOriginal?.transactionReferenceId !==
                          transactionUpdated.transactionReferenceId
                            ? {
                                transactionReferenceId:
                                  transactionUpdated.transactionReferenceId,
                              }
                            : {}),
                          ...(selectedBookkeepingFull?.accountingSystem ===
                          AccountingSystem.bexio
                            ? {
                                status: TransactionStatus.draft,
                              }
                            : {}),
                          // TODO Update taxes
                        },
                      },
                    });
                  }
                );

                await Promise.all(transactionUpdatePromises);

                // Update balance if ÄT
                if (
                  selectedOrganization?.logo === aerzteTreuhandLogo &&
                  selectedBookkeepingFull
                ) {
                  const bookkeepingYearsAmount = new Map<
                    BookkeepingYear,
                    number
                  >();
                  // Same filter as the save loop above: extracted rows are
                  // read-only in edit mode and never posted to the Saldo.
                  for (const transactionUpdated of transactionsUpdated.filter(
                    (t) => t.status !== TransactionStatus.extracted
                  )) {
                    const transactionOriginal = transactions?.find(
                      (t) => t.id === transactionUpdated.id
                    );
                    const bookkeepingYear = getBookkeepingYear(
                      selectedBookkeepingFull,
                      getLocalDate(new Date(transactionUpdated.paidAt))
                    );
                    if (bookkeepingYear && transactionOriginal) {
                      const amountOld =
                        transactionOriginal.bookingAccount.taxType ===
                        TaxType.earnings
                          ? transactionOriginal.amount
                          : -transactionOriginal.amount;
                      const amountNew =
                        transactionUpdated.bookingAccount.taxType ===
                        TaxType.earnings
                          ? transactionUpdated.amount
                          : -transactionUpdated.amount;
                      const amount = amountNew - amountOld;

                      if (bookkeepingYearsAmount.has(bookkeepingYear)) {
                        bookkeepingYearsAmount.set(
                          bookkeepingYear,
                          bookkeepingYearsAmount.get(bookkeepingYear)! + amount
                        );
                      } else {
                        bookkeepingYearsAmount.set(bookkeepingYear, amount);
                      }

                      // Also update next bookkeepingYear if transaction was from a previous year
                      const nextBookkeepingYear = getBookkeepingYear(
                        selectedBookkeepingFull,
                        getLocalDate(new Date())
                      );
                      if (
                        nextBookkeepingYear &&
                        nextBookkeepingYear.year > bookkeepingYear?.year
                      ) {
                        if (bookkeepingYearsAmount.has(nextBookkeepingYear)) {
                          bookkeepingYearsAmount.set(
                            nextBookkeepingYear,
                            bookkeepingYearsAmount.get(nextBookkeepingYear)! +
                              amount
                          );
                        } else {
                          bookkeepingYearsAmount.set(
                            nextBookkeepingYear,
                            amount
                          );
                        }
                      }

                      await updateBalances(bookkeepingYearsAmount);
                    }
                  }
                }

                setUpdating(false);
                setEditing(false);
                refreshTransactions();
              }}
            >
              Speichern
            </Button>
          )}
          {user &&
            selectedOrganization &&
            showSettings(user, selectedOrganization) && (
              <Tooltip
                label={`Einstellungen ${
                  selectedOrganization?.logo === aerzteTreuhandLogo
                    ? "Kassenbuch"
                    : "Buchhaltung"
                }`}
              >
                <IconButton
                  variant="outline"
                  aria-label="Bookkeeping settings"
                  icon={<Image boxSize="20px" src={IconSettings} />}
                  onClick={() => {
                    navigate(routeSettingsBookkeeping);
                  }}
                />
              </Tooltip>
            )}
        </Flex>
      </Flex>
      {balanceData && (
        <Card width="full" borderRadius={12}>
          <CardBody color="gray.900" fontWeight="semibold" fontSize={36}>
            <Text color="gray.600" fontWeight="medium" fontSize={14}>
              Saldo {balanceData.year}
            </Text>
            <Text>{formatAmount(balanceData.balance, CurrencyCode.chf)}</Text>
          </CardBody>
        </Card>
      )}
      <DataTable
        typeNamePlural={typeNamePlural}
        columns={columns}
        data={filteredTransactions}
        state={state}
        // Row-click replaces the former per-row «Prüfen» button. Every row
        // opens the verify modal — extracted to verify, anything else to edit
        // or read-only per the app's editingEnabled gate (verifyMode /
        // verifyReadOnly). Not while bulk-editing, where a click means
        // "interact with the inline fields"; deleted rows stay inert in
        // DraggableRow.
        rowAction={(transaction) =>
          editing ? undefined : () => openVerifyForTransaction(transaction)
        }
        error={
          selectedOrganization?.type === OrganizationType.accountant &&
          organizations &&
          organizations.length === 1 ? (
            <NoOrganization />
          ) : undefined
        }
        footer={footerRow}
      />
      <DropzoneOverlay
        isEnabled={uploadEnabled}
        onDropFiles={handleDroppedFiles}
      />
      <VerifyReceiptModal
        isOpen={verifyModalOpen}
        onClose={closeVerifyModal}
        isLoading={verifyLoading}
        initialValues={verifyInitialValues}
        previewUrl={verifyPreview?.url}
        previewPending={verifyPreviewPending}
        previewIsPdf={verifyPreview?.isPdf}
        bookkeeping={selectedBookkeepingFull}
        optionsBookingAccounts={optionsBookingAccounts}
        optionsCurrencyCodes={
          hasCurrencies
            ? optionsCurrencyCodesEffective
            : [{ value: CurrencyCode.chf, label: "CHF" }]
        }
        optionsTransactionReferences={verifyOptionsTransactionReferences}
        mode={verifyMode}
        readOnly={verifyReadOnly}
        onSave={handleVerifySave}
      />
    </VStack>
  );
};

export default Transactions;
